import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Collapse, Form, Grid, Modal, Select, Space, Spin } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PATHS } from "../../../app/router/paths";
import { appNotification } from "../../../shared/notifications/appNotification";
import { useAuth } from "../../auth/model/AuthContext";
import {
  getProposeTemplate,
  getProposeTemplateErrorMessage,
  getProposeTemplatePreview,
  createProposeSubmissionGoogleSheet,
  listProposeTemplates,
  submitProposeTemplate,
  type ProposeTemplate,
  type ProposeTemplateFormDefinition,
  type ProposeTemplatePreview,
} from "../api/proposeTemplatesApi";
import { PageHeader } from "../../../shared/components/PageHeader";
import { ProposeBasicInfoFields } from "../components/ProposeBasicInfoFields";
import { ProposeTemplateOrderFields } from "../components/ProposeTemplateOrderFields";
import { useProposeEditor } from "../hooks/useProposeEditor";
import { TemplateSheetPreview } from "./ProposeTemplateManagementPage";
import "../styles/Proposes.css";

/** The coordinator assigns only products. Commercial item fields are owned by Sale. */
export const ProposeEditPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const coordinatorManage =
    user?.permissions.includes("propose.coordinator.manage") ?? false;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const id = useMemo(() => Number(params.get("id")) || undefined, [params]);
  const representativeId = params.get("representative_id") || undefined;
  const {
    form, customers, recipients, products, productOptions, mobileItems, loading,
    saving, searchProductOptions, save,
  } = useProposeEditor({ id, representativeId, isMobile });
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productCodesToAdd, setProductCodesToAdd] = useState<string[]>([]);
  const [templates, setTemplates] = useState<ProposeTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templatePreview, setTemplatePreview] = useState<ProposeTemplatePreview | null>(null);
  const [templatePreviewCache, setTemplatePreviewCache] = useState<
    Record<number, ProposeTemplatePreview>
  >({});
  const [templateForm] = Form.useForm();
  const [invalidTemplateHeaderIds, setInvalidTemplateHeaderIds] = useState<Set<string>>(new Set());
  const [templateOrderFormExpanded, setTemplateOrderFormExpanded] = useState(true);
  const [pendingHeaderFocusId, setPendingHeaderFocusId] = useState<string | null>(null);
  const [submissionSaving, setSubmissionSaving] = useState(false);
  const [templateDefinition, setTemplateDefinition] =
    useState<ProposeTemplateFormDefinition | null>(null);
  const [templateDefinitionLoading, setTemplateDefinitionLoading] = useState(false);
  const [templateListLoading, setTemplateListLoading] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingHeaderFocusId || !templateOrderFormExpanded) return;
    const timeoutId = window.setTimeout(() => {
      templateForm.scrollToField(pendingHeaderFocusId, { focus: true });
      setPendingHeaderFocusId(null);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [pendingHeaderFocusId, templateForm, templateOrderFormExpanded]);

  useEffect(() => {
    if (!coordinatorManage) return;
    let active = true;
    setTemplateListLoading(true);
    listProposeTemplates()
      .then((items) => {
        if (active) setTemplates(items);
      })
      .catch((error) => {
        if (active) setTemplateError(getProposeTemplateErrorMessage(error));
      })
      .finally(() => {
        if (active) setTemplateListLoading(false);
      });
    return () => {
      active = false;
    };
  }, [coordinatorManage]);

  const selectTemplate = async (templateId?: number) => {
    setSelectedTemplateId(templateId ?? null);
    setTemplatePreview(null);
    setTemplateDefinition(null);
    setTemplateError(null);
    setInvalidTemplateHeaderIds(new Set());
    setPendingHeaderFocusId(null);
    templateForm.resetFields();
    if (templateId === undefined) return;

    setTemplateDefinitionLoading(true);
    try {
      const definition = await getProposeTemplate(templateId);
      setTemplateDefinition(definition);
      const values: Record<string, unknown> = {};
      (definition.headerFields ?? []).forEach((field) => {
        if (field.defaultValue !== undefined) values[field.id] = field.defaultValue;
      });
      templateForm.setFieldsValue(values);
    } catch (error) {
      setTemplateError(getProposeTemplateErrorMessage(error));
    } finally {
      setTemplateDefinitionLoading(false);
    }
  };

  const openTemplatePreview = async () => {
    if (selectedTemplateId === null) return;
    const cachedPreview = templatePreviewCache[selectedTemplateId];
    if (cachedPreview) {
      setTemplatePreview(cachedPreview);
      setTemplatePreviewOpen(true);
      return;
    }
    setTemplatePreviewLoading(true);
    setTemplateError(null);
    try {
      const preview = await getProposeTemplatePreview(selectedTemplateId);
      setTemplatePreview(preview);
      setTemplatePreviewCache((cache) => ({
        ...cache,
        [selectedTemplateId]: preview,
      }));
      setTemplatePreviewOpen(true);
    } catch (error) {
      setTemplateError(getProposeTemplateErrorMessage(error));
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  const saveProposal = async (status: "DRAFT" | "PUBLISH") => {
    if (coordinatorManage) {
      if (selectedTemplateId === null || templateDefinition === null) {
        appNotification.error({ message: "テンプレートを選択してください" });
        return;
      }
      const requiredFields = [
        "order_name",
        "representative_id",
        "recipient_id",
        "expires_at",
      ];
      try {
        await form.validateFields(requiredFields);
      } catch (validationError) {
        const errorFields = (
          validationError as { errorFields?: Array<{ name: Array<string | number> }> }
        ).errorFields ?? [];
        const firstInvalidField = errorFields[0];
        if (firstInvalidField) {
          form.scrollToField(firstInvalidField.name, { focus: true });
        }
        appNotification.error({
          message: firstInvalidField?.name[0] === "order_name"
            ? "Order Nameを入力してください"
            : "必須項目を入力してください",
        });
        return;
      }
      const orderName = String(form.getFieldValue("order_name") ?? "").trim();
      const productCodes = (form.getFieldValue("items") ?? [])
        .map((item: { product_code?: unknown }) => item.product_code)
        .filter((code: unknown): code is string => typeof code === "string" && code.trim().length > 0)
        .map((code: string) => code.trim());
      try {
        const headerFields = templateDefinition.headerFields ?? [];
        const currentHeaderValues = templateForm.getFieldsValue(true) as Record<string, unknown>;
        if (!productCodes.length) {
          requestAnimationFrame(() => {
            const addProductButton = document.getElementById("propose-add-product");
            addProductButton?.scrollIntoView({ behavior: "smooth", block: "center" });
            addProductButton?.focus();
          });
          appNotification.error({ message: "対象商品を少なくとも1つ追加してください" });
          return;
        }
        const headers = Object.fromEntries(
          headerFields.map((field) => [
            field.id,
            currentHeaderValues[field.id] ?? field.defaultValue ?? "",
          ]),
        );
        const formValues = form.getFieldsValue(true);
        const selectedRecipient = recipients.find(
          (recipient) => String(recipient.user_id) === String(formValues.recipient_id),
        );
        const deadline = formValues.expires_at ? dayjs(formValues.expires_at) : null;
        if (!selectedRecipient || !deadline?.isValid()) {
          appNotification.error({ message: "買受人担当者名と営業入力期限を確認してください" });
          return;
        }
        setSubmissionSaving(true);
        const submission = {
          order_name: orderName,
          product_code: productCodes,
          headers,
          representative_id: String(formValues.representative_id),
          representative_name: selectedRecipient.display_name || selectedRecipient.email,
          deadline: deadline.toISOString(),
        } as const;
        const createdSubmission = await submitProposeTemplate(
          selectedTemplateId,
          submission,
        );
        if (!createdSubmission.success || !createdSubmission.submission_id) {
          appNotification.error({
            message: createdSubmission.detail || "注文を作成できませんでした",
          });
          return;
        }
        try {
          await createProposeSubmissionGoogleSheet(createdSubmission.submission_id);
        } catch (googleSheetError) {
          appNotification.error({
            message: `注文は作成されましたが、Google Sheetsの作成または変換に失敗しました: ${getProposeTemplateErrorMessage(googleSheetError)}`,
          });
          navigate(PATHS.PROPOSES.CREATE);
          return;
        }
        appNotification.success({
          message: "注文を作成しました",
        });
        navigate(PATHS.PROPOSES.CREATE, {
          state: { googleSheetSubmissionId: createdSubmission.submission_id },
        });
      } catch (error) {
        appNotification.error({ message: getProposeTemplateErrorMessage(error) });
      } finally {
        setSubmissionSaving(false);
      }
      return;
    }
    const proposalId = await save(status);
    if (proposalId) navigate(`${PATHS.PROPOSES.VIEW}?id=${proposalId}`);
  };
  const selectedCodes = new Set(mobileItems.map((item) => item.product_code).filter(Boolean));
  const selectableOptions = (currentCode?: string) => productOptions.map((option) => ({
    ...option,
    disabled: option.value !== currentCode && selectedCodes.has(option.value),
  }));
  const openProductPicker = () => {
    setProductCodesToAdd([]);
    setProductPickerOpen(true);
    if (!products.length) void searchProductOptions();
  };

  const pageTitle = id ? "提案を編集" : "新規提案を作成";
  const pageDescription = id
    ? "基本情報と対象商品のみ登録します。価格・供給予定は担当 Sale が入力します。"
    : "基本情報と対象商品のみ登録します。価格・供給予定は担当 Sale が入力します。";

  if (loading) return <div className="propose-loading"><Spin /></div>;

  const productEditor = () => (
    <Form.List name="items">
      {(fields, { add, remove }) => (
        <>
          <div className="propose-product-toolbar">
            <div>
              <strong>担当営業が登録済みの商品</strong>
              <span>選択時点の担当 Sale を提案に固定します。</span>
            </div>
            <Button id="propose-add-product" icon={<PlusOutlined />} onClick={openProductPicker}>商品を追加</Button>
          </div>
          {(() => {
            const productRows = fields.map((field) => {
              const value = mobileItems[field.name] || {};
              const product = productOptions.find((option) => option.value === value.product_code);
              return {
                key: field.key,
                field,
                product_code: value.product_code || "—",
                product_name: product?.name || value.product_name || "—",
              };
            });
            return <section className="propose-product-assignments" aria-label="対象商品">
              <div className="propose-product-list-header">
                <strong>対象商品</strong>
              </div>
            <div className="propose-product-assignment-list">
              {productRows.map((row) => <article className="propose-product-assignment" key={row.key}>
                <div className="propose-product-assignment-copy"><strong>{row.product_code}</strong><span>{row.product_name}</span></div>
                <button
                  type="button"
                  className="propose-product-remove"
                  aria-label={`${row.product_name}を対象商品から削除`}
                  title="削除"
                  onClick={() => remove(row.field.name)}
                >
                  ×
                </button>
              </article>)}
              {!fields.length && <div className="propose-product-empty">「商品を追加」から対象商品を選択してください。</div>}
            </div>
            </section>;
          })()}
          <Modal
            open={productPickerOpen}
            title="対象商品を追加"
            okText="選択した商品を追加"
            cancelText="キャンセル"
            okButtonProps={{ disabled: !productCodesToAdd.length }}
            onCancel={() => setProductPickerOpen(false)}
            onOk={() => {
              productCodesToAdd.forEach((productCode) => {
                const product = productOptions.find((option) => option.value === productCode);
                add({ product_code: productCode, product_name: product?.name || "" });
              });
              setProductPickerOpen(false);
              setProductCodesToAdd([]);
            }}
          >
            <p className="propose-product-picker-help">担当営業が登録済みの商品だけを選択できます。追加済みの商品は選択できません。</p>
            <Select
              mode="multiple"
              showSearch
              allowClear
              autoFocus
              style={{ width: "100%" }}
              placeholder="商品コード・商品名で検索"
              value={productCodesToAdd}
              options={selectableOptions()}
              filterOption={false}
              onSearch={searchProductOptions}
              onChange={setProductCodesToAdd}
            />
          </Modal>
        </>
      )}
    </Form.List>
  );

  return (
    <main className={isMobile ? "propose-mobile-editor-page propose-coordinator-editor" : "propose-editor"}>
      {isMobile ? (
        <div className="propose-mobile-form-content">
          <div>
            <h1>{pageTitle}</h1>
            <span>{pageDescription}</span>
          </div>
        </div>
      ) : (
        <PageHeader
          title={pageTitle}
          description={pageDescription}
          isbutton={false}
        />
      )}
      {coordinatorManage && (
        <>
          <section className="propose-section">
            <h3>テンプレート</h3>
            <div className="propose-section-body">
              <label style={{ display: "block", marginBottom: 8 }}>テンプレートを選択</label>
              <Space.Compact block>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={templateListLoading || templateDefinitionLoading}
                  value={selectedTemplateId ?? undefined}
                  placeholder="準備完了のテンプレートを選択"
                  options={templates
                    .filter((template) => template.status === 1)
                    .map((template) => ({
                      value: template.id,
                      label: template.name,
                    }))}
                  onChange={(value: number | undefined) => void selectTemplate(value)}
                  style={{ flex: 1 }}
                />
                <Button
                  icon={<EyeOutlined />}
                  aria-label="テンプレートをプレビュー"
                  title="テンプレートをプレビュー"
                  disabled={selectedTemplateId === null}
                  loading={templatePreviewLoading}
                  onClick={() => void openTemplatePreview()}
                />
              </Space.Compact>
              {templateError && (
                <Alert
                  type="error"
                  showIcon
                  message={templateError}
                  style={{ marginBottom: 16 }}
                />
              )}
            </div>
          </section>
        </>
      )}
      <Form form={form} layout="vertical" className="propose-form">
        <section className="propose-section">
          <h3>提案基本情報</h3>
          <div className="propose-section-body"><ProposeBasicInfoFields variant={isMobile ? "mobile" : "desktop"} customers={customers} recipients={recipients} fieldSet={coordinatorManage ? "coordinator-basic" : "all"} /></div>
        </section>
      </Form>
      {coordinatorManage && !templateDefinitionLoading && templateDefinition ? (
        <Form
          form={templateForm}
          layout="vertical"
          className="propose-form"
        >
          <section className="propose-section" style={{ display: "none" }}>
            <Collapse
              activeKey={templateOrderFormExpanded ? ["template-order-form"] : []}
              onChange={(keys) =>
                setTemplateOrderFormExpanded(
                  Array.isArray(keys)
                    ? keys.includes("template-order-form")
                    : keys === "template-order-form",
                )
              }
              items={[
                {
                  key: "template-order-form",
                  label: "",
                  children: (
                    <div className="propose-section-body">
                      <ProposeTemplateOrderFields
                        definition={templateDefinition}
                        invalidFieldIds={invalidTemplateHeaderIds}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </section>
        </Form>
      ) : null}
      <Form form={form} layout="vertical" className="propose-form" id="propose-items-form">
        <section className="propose-section">
          <h3>対象商品</h3>
          <div className="propose-section-body">{productEditor()}</div>
        </section>
      </Form>
      <Modal
        title={`Excelプレビュー：${templatePreview?.name ?? ""}`}
        open={templatePreviewOpen}
        onCancel={() => setTemplatePreviewOpen(false)}
        width="96vw"
        style={{ top: 20, maxWidth: 1200 }}
        footer={<Button onClick={() => setTemplatePreviewOpen(false)}>閉じる</Button>}
        destroyOnClose
      >
        {templatePreviewLoading ? (
          <Spin />
        ) : (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {templatePreview?.sheets.map((sheet) => (
              <Card key={sheet.name} size="small" title={sheet.name}>
                <TemplateSheetPreview sheet={sheet} readOnly />
              </Card>
            ))}
          </Space>
        )}
      </Modal>
      <div className={isMobile ? "propose-mobile-editor-actions" : "propose-actions"}>
        <Button onClick={() => navigate(PATHS.PROPOSES.ROOT)}>キャンセル</Button>
        <Space>
          {coordinatorManage ? null : (
            <Button loading={saving || submissionSaving} onClick={() => void saveProposal("DRAFT")}>下書き保存</Button>
          )}
          <Button type="primary" loading={saving || submissionSaving} onClick={() => void saveProposal("PUBLISH")}>
            {coordinatorManage ? "営業へ公開" : "営業へ公開"}
          </Button>
        </Space>
      </div>
    </main>
  );
};
